/* ==========================================================================
   Churn Metrics Component
   Task F37: Churn rate, MRR, tier breakdown, and churned pharmacies table
   ========================================================================== */

import type { ChurnMetrics as ChurnMetricsType, TierBreakdown } from '@/api/internal'

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

const TIER_COLORS: Record<string, string> = {
  starter: 'bg-blue-500',
  growth: 'bg-purple-500',
  scale: 'bg-amber-500',
}

const TIER_LABELS: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
}

function churnRateColor(rate: number): string {
  if (rate < 5) return 'text-green-700 bg-green-50'
  if (rate <= 10) return 'text-amber-700 bg-amber-50'
  return 'text-red-700 bg-red-50'
}

const CURRENCY_SYMBOLS: Record<string, string> = { usd: '$', cad: 'CA$' }

function formatCurrency(v: number, currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency] ?? '$'
  return `${symbol}${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/* --------------------------------------------------------------------------
   Skeleton
   -------------------------------------------------------------------------- */

function SkeletonChurn() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 sm:p-5"
          >
            <div className="h-4 w-24 bg-gray-200 rounded mb-3" />
            <div className="h-8 w-20 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Tier Breakdown Bar
   -------------------------------------------------------------------------- */

function TierBar({ tiers, total }: { tiers: TierBreakdown[]; total: number }) {
  if (total === 0) {
    return (
      <p className="text-sm text-charcoal/60">No active subscriptions.</p>
    )
  }

  return (
    <div>
      {/* Bar */}
      <div className="flex h-6 rounded-full overflow-hidden">
        {tiers.map((t) => {
          const pct = (t.count / total) * 100
          return (
            <div
              key={t.tier}
              className={`${TIER_COLORS[t.tier] || 'bg-gray-400'}`}
              style={{ width: `${pct}%` }}
              title={`${TIER_LABELS[t.tier] || t.tier}: ${t.count} (${pct.toFixed(0)}%)`}
            />
          )
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-3">
        {tiers.map((t) => (
          <div key={t.tier} className="flex items-center gap-1.5">
            <span
              className={`inline-block h-3 w-3 rounded-full ${TIER_COLORS[t.tier] || 'bg-gray-400'}`}
            />
            <span className="text-sm text-charcoal/70">
              {TIER_LABELS[t.tier] || t.tier}: {t.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Main Component
   -------------------------------------------------------------------------- */

interface ChurnMetricsProps {
  data: ChurnMetricsType | null
  loading: boolean
}

export function ChurnMetrics({ data, loading }: ChurnMetricsProps) {
  if (loading) return <SkeletonChurn />
  if (!data) return null

  // Currencies are reported side by side, never summed — there is no FX rate.
  // Zero buckets are hidden so an all-USD book renders exactly as before.
  const mrrEntries = (data.mrr_by_currency ?? []).filter((c) => c.amount > 0)
  const primaryMrr = mrrEntries[0] ?? { currency: 'usd', amount: data.mrr }
  const otherMrr = mrrEntries.slice(1)

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Churn rate */}
        <div className="bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 sm:p-5">
          <p className="text-sm text-charcoal/60 mb-1">Churn Rate (90d)</p>
          <p
            className={`text-2xl font-bold px-2 py-1 rounded inline-block ${churnRateColor(data.churn_rate_90d)}`}
          >
            {data.churn_rate_90d.toFixed(1)}%
          </p>
        </div>

        {/* MRR */}
        <div className="bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 sm:p-5">
          <p className="text-sm text-charcoal/60 mb-1">
            Monthly Recurring Revenue
          </p>
          <p className="text-2xl font-bold text-charcoal">
            {formatCurrency(primaryMrr.amount, primaryMrr.currency)}
          </p>
          {otherMrr.map((c) => (
            <p
              key={c.currency}
              className="text-sm font-semibold text-charcoal/70 mt-0.5"
            >
              {formatCurrency(c.amount, c.currency)}
            </p>
          ))}
        </div>

        {/* Active subs */}
        <div className="bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 sm:p-5">
          <p className="text-sm text-charcoal/60 mb-1">Active Subscriptions</p>
          <p className="text-2xl font-bold text-charcoal">
            {data.total_active_subscriptions}
          </p>
        </div>
      </div>

      {/* Tier breakdown */}
      <div className="bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 sm:p-6">
        <h3 className="text-sm font-semibold text-charcoal mb-3">
          Tier Breakdown
        </h3>
        <TierBar tiers={data.active_by_tier} total={data.total_active_subscriptions} />
      </div>

      {/* Churned pharmacies table */}
      {data.churned_pharmacies.length > 0 && (
        <div className="bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 sm:p-6">
          <h3 className="text-sm font-semibold text-charcoal mb-3">
            Churned Pharmacies (last 90 days)
          </h3>
          <div className="overflow-x-auto -mx-4 sm:-mx-6">
            <table className="w-full min-w-[540px]">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-xs font-medium text-charcoal/60 uppercase tracking-wider px-4 sm:px-6 py-3">
                    Pharmacy
                  </th>
                  <th className="text-left text-xs font-medium text-charcoal/60 uppercase tracking-wider px-4 sm:px-6 py-3">
                    Tier
                  </th>
                  <th className="text-right text-xs font-medium text-charcoal/60 uppercase tracking-wider px-4 sm:px-6 py-3">
                    Days Active
                  </th>
                  <th className="text-right text-xs font-medium text-charcoal/60 uppercase tracking-wider px-4 sm:px-6 py-3">
                    Total Rxs
                  </th>
                  <th className="text-left text-xs font-medium text-charcoal/60 uppercase tracking-wider px-4 sm:px-6 py-3">
                    Canceled
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.churned_pharmacies.map((cp) => (
                  <tr key={cp.pharmacy_id} className="hover:bg-gray-50">
                    <td className="px-4 sm:px-6 py-3 text-sm font-medium text-charcoal">
                      {cp.pharmacy_name}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-sm text-charcoal/70 capitalize">
                      {cp.tier_at_cancellation}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-sm text-charcoal/70 text-right">
                      {cp.days_active}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-sm text-charcoal/70 text-right">
                      {cp.total_scripts}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-sm text-charcoal/70">
                      {new Date(cp.cancellation_date).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
